// @intent: Shared contracts for F3 preview/export delivery API payloads and validation metadata.
// @flow: Used by runtime/web route schemas and AI Generate deliver manifests.

import { z } from 'zod/v4';

export const ExportTargetProfileSchema = z.enum(['preview', 'unity']);
export type ExportTargetProfile = z.infer<typeof ExportTargetProfileSchema>;

export const ExportStatusSchema = z.enum(['building', 'ready', 'failed']);
export type ExportStatus = z.infer<typeof ExportStatusSchema>;

export const ExportValidationReportV1Schema = z.strictObject({
  version: z.literal('v1'),
  targetProfile: ExportTargetProfileSchema,
  pass: z.boolean(),
  blockers: z.array(z.string().trim().min(1)),
  warnings: z.array(z.string().trim().min(1)),
  checks: z.record(z.string(), z.boolean()),
  sizeBytes: z.number().int().nonnegative(),
  entryFile: z.string().trim().min(1),
  assetCount: z.number().int().nonnegative(),
});
export type ExportValidationReportV1 = z.infer<typeof ExportValidationReportV1Schema>;

export const UnityExportComplianceV1Schema = z.strictObject({
  target: z.literal('unity'),
  status: z.enum(['passed', 'failed']),
  sizeBytes: z.number().int().nonnegative(),
  singleHtml: z.boolean(),
  inlined: z.boolean(),
  minified: z.boolean(),
  mraidPresent: z.boolean(),
  mraidOpen: z.boolean(),
  noAutoRedirect: z.boolean(),
  noRequiredNetworkRequests: z.boolean(),
  viewableChange: z.boolean(),
  portrait: z.boolean(),
  landscape: z.boolean(),
});
export type UnityExportComplianceV1 = z.infer<typeof UnityExportComplianceV1Schema>;

export const ProjectExportSummaryV1Schema = z.strictObject({
  id: z.uuid(),
  projectId: z.uuid(),
  commitId: z.uuid(),
  targetProfile: ExportTargetProfileSchema,
  targetPlatform: z.string().nullable(),
  status: ExportStatusSchema,
  bundleUrl: z.string().trim().min(1).optional(),
  sizeBytes: z.number().int().nonnegative(),
  checksumSha256: z.string().trim().min(1).nullable(),
  entryFile: z.string().trim().min(1),
  assetCount: z.number().int().nonnegative(),
  validationReport: ExportValidationReportV1Schema,
  createdAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable().optional(),
  downloadUrl: z.string().trim().min(1).optional(),
});
export type ProjectExportSummaryV1 = z.infer<typeof ProjectExportSummaryV1Schema>;

export const ProjectPreviewResponseV1Schema = z.strictObject({
  previewUrl: z.string().trim().min(1),
  exportId: z.uuid(),
  commitId: z.uuid(),
  status: z.literal('ready'),
  validationReport: ExportValidationReportV1Schema,
});
export type ProjectPreviewResponseV1 = z.infer<typeof ProjectPreviewResponseV1Schema>;

export const ProjectExportBuildRequestV1Schema = z.strictObject({
  targetProfile: ExportTargetProfileSchema.default('unity'),
});
export type ProjectExportBuildRequestV1 = z.infer<typeof ProjectExportBuildRequestV1Schema>;
